import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { money } from "../lib/format";
import { maskMoney, usePrivacy } from "../lib/privacy";
import type { Metrics } from "../lib/types";

async function fetchMetrics(): Promise<Metrics> {
  const { data, error } = await supabase.from("v_revenue_metrics").select("*").single();
  if (error) throw new Error(error.message);
  return data as Metrics;
}

function Metric({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="metric" title={title}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}

/**
 * Header strip over every page: Today / This week (ISO Mon–Sun) / This month /
 * All-time / Paying clients / Avg LTV, all straight from v_revenue_metrics.
 * Refreshes once a minute.
 *
 * The 👁/🙈 button at the right edge toggles privacy mode (lib/privacy.ts):
 * every DOLLAR figure app-wide renders as "$ ••••" so the owner can share the
 * screen without exposing financials. Counts stay visible, and the queue's 💳
 * credit indicator is never masked (operational).
 */
export function MetricsBar() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const { data, error } = useQuery<Metrics>(fetchMetrics, [tick]);
  const [privacy, setPrivacy] = usePrivacy();

  const v = (f: (m: Metrics) => string): string => (data ? f(data) : "…");
  /** Dollar metrics run through the privacy mask; counts use `v` directly. */
  const dollars = (f: (m: Metrics) => number | string | null): string =>
    data ? maskMoney(money(f(data)), privacy) : "…";

  return (
    <header className="metrics-bar" aria-label="Revenue metrics">
      <Metric label="Today" value={dollars((m) => m.today_revenue)} />
      <Metric
        label="This week"
        value={dollars((m) => m.week_revenue)}
        title="ISO week, Mon–Sun (NY)"
      />
      <Metric label="This month" value={dollars((m) => m.month_revenue)} />
      <Metric
        label="All-time"
        value={dollars((m) => m.alltime_revenue)}
        title={
          data ? `Refunds all-time: ${maskMoney(money(data.alltime_refunds), privacy)}` : undefined
        }
      />
      <Metric
        label="Paying clients"
        value={v((m) => String(m.paying_clients))}
        title={data ? `${data.total_clients} clients total` : undefined}
      />
      <Metric label="Avg LTV" value={dollars((m) => m.avg_ltv)} />
      {error && (
        <span className="metrics-error" title={error}>
          metrics unavailable
        </span>
      )}
      <button
        type="button"
        className="btn btn-ghost btn-icon metrics-eye"
        onClick={() => setPrivacy(!privacy)}
        aria-pressed={privacy}
        aria-label={privacy ? "Show dollar figures" : "Hide dollar figures"}
        title={
          privacy
            ? "Privacy mode is ON — dollar figures are hidden. Click to show them."
            : "Hide all dollar figures (privacy mode for screen-sharing)"
        }
      >
        <span aria-hidden="true">{privacy ? "🙈" : "👁"}</span>
      </button>
    </header>
  );
}

export default MetricsBar;
